import { useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
} from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChevronDown, Search } from "lucide-react-native";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenTitle,
  ScreenSubtitle,
  ContinueButton,
  GLASS_CONTROL_INPUT_STYLE,
  GLASS_CONTROL_SHELL_STYLE,
} from "../components/OnboardingLayout";
import { useLocale, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { normalizeCountryCode } from "@src/packages/location/countryPreference";
import { useDetectedCountry } from "@src/packages/location/useDetectedCountry";
import {
  COUNTRIES as COUNTRY_METADATA,
  getCountryName,
  type CountryMetadata,
} from "@src/packages/location/countryMetadata";
import type { OnboardingStackParamList } from "../types";
import {
  isPostalCodeRequired,
  isPostalCodeValid,
  normalizePostalCode,
} from "../locationValidation";
import { FieldMessage, SupportingSubtitleText } from "@louise/moon-ui";
import { semanticColors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<OnboardingStackParamList, "Location">;

type Country = CountryMetadata & {
  postalLabel: string;
  postalPlaceholder: string;
  postalMaxLength: number;
  postalKeyboard: "number-pad" | "default";
  postalRequired: boolean;
};

type PostalMetadata = Pick<
  Country,
  "postalLabel" | "postalPlaceholder" | "postalMaxLength" | "postalKeyboard"
>;

const POSTAL_METADATA: Record<string, PostalMetadata> = {
  US: {
    postalLabel: "ZIP code",
    postalPlaceholder: "e.g. 10001",
    postalMaxLength: 5,
    postalKeyboard: "number-pad",
  },
  GB: {
    postalLabel: "Postcode",
    postalPlaceholder: "e.g. SW1A 1AA",
    postalMaxLength: 8,
    postalKeyboard: "default",
  },
  CA: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. M5V 3L9",
    postalMaxLength: 7,
    postalKeyboard: "default",
  },
  AU: {
    postalLabel: "Postcode",
    postalPlaceholder: "e.g. 2000",
    postalMaxLength: 4,
    postalKeyboard: "number-pad",
  },
  DE: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 10115",
    postalMaxLength: 5,
    postalKeyboard: "number-pad",
  },
  FR: {
    postalLabel: "Code postal",
    postalPlaceholder: "e.g. 75001",
    postalMaxLength: 5,
    postalKeyboard: "number-pad",
  },
  ES: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 28001",
    postalMaxLength: 5,
    postalKeyboard: "number-pad",
  },
  IT: {
    postalLabel: "CAP",
    postalPlaceholder: "e.g. 00100",
    postalMaxLength: 5,
    postalKeyboard: "number-pad",
  },
  NL: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 1011 AB",
    postalMaxLength: 7,
    postalKeyboard: "default",
  },
  SE: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 111 22",
    postalMaxLength: 6,
    postalKeyboard: "number-pad",
  },
  CH: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 8001",
    postalMaxLength: 4,
    postalKeyboard: "number-pad",
  },
  IE: {
    postalLabel: "Eircode",
    postalPlaceholder: "e.g. D02 AF30",
    postalMaxLength: 8,
    postalKeyboard: "default",
  },
  NZ: {
    postalLabel: "Postcode",
    postalPlaceholder: "e.g. 1010",
    postalMaxLength: 4,
    postalKeyboard: "number-pad",
  },
  SG: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 018956",
    postalMaxLength: 6,
    postalKeyboard: "number-pad",
  },
  JP: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 100-0001",
    postalMaxLength: 8,
    postalKeyboard: "default",
  },
  KR: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 03171",
    postalMaxLength: 5,
    postalKeyboard: "number-pad",
  },
  IN: {
    postalLabel: "PIN code",
    postalPlaceholder: "e.g. 110001",
    postalMaxLength: 6,
    postalKeyboard: "number-pad",
  },
  AE: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 00000",
    postalMaxLength: 5,
    postalKeyboard: "number-pad",
  },
  IL: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 6100000",
    postalMaxLength: 7,
    postalKeyboard: "number-pad",
  },
  BR: {
    postalLabel: "CEP",
    postalPlaceholder: "e.g. 01001-000",
    postalMaxLength: 9,
    postalKeyboard: "default",
  },
  MX: {
    postalLabel: "Código postal",
    postalPlaceholder: "e.g. 06600",
    postalMaxLength: 5,
    postalKeyboard: "number-pad",
  },
  ZA: {
    postalLabel: "Postal code",
    postalPlaceholder: "e.g. 2000",
    postalMaxLength: 4,
    postalKeyboard: "number-pad",
  },
};

const COUNTRIES: Country[] = COUNTRY_METADATA.map((metadata) => {
  const postal = POSTAL_METADATA[metadata.code];
  return {
    ...metadata,
    postalLabel: postal?.postalLabel ?? "Postal code",
    postalPlaceholder: postal?.postalPlaceholder ?? "Postal code",
    postalMaxLength: postal?.postalMaxLength ?? 16,
    postalKeyboard: postal?.postalKeyboard ?? "default",
    postalRequired: isPostalCodeRequired(metadata.code),
  };
});

const SUPPORTED_COUNTRY_CODES = COUNTRIES.map(({ code }) => code);

function findCountry(code: string | null | undefined): Country | null {
  const normalizedCode = normalizeCountryCode(code);
  return (
    COUNTRIES.find((candidate) => candidate.code === normalizedCode) ?? null
  );
}

function getDefaultCountry(): Country {
  return findCountry("US") ?? COUNTRIES[0]!;
}

export default function LocationScreen({ navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("LocationScreen");
  const { locale } = useLocale();
  const { data, updateField } = useOnboarding();
  const savedCountryCode = normalizeCountryCode(data.country);
  const detected = useDetectedCountry({
    savedCountry: savedCountryCode,
    supportedCountryCodes: SUPPORTED_COUNTRY_CODES,
  });
  const [country, setCountry] = useState<Country>(
    () => findCountry(detected.countryCode) ?? getDefaultCountry(),
  );
  const hasCountryInteraction = useRef(savedCountryCode !== null);
  const hasPostalInteraction = useRef(Boolean(data.zipCode));
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const countrySearchInputRef = useRef<TextInput | null>(null);
  const [countryQuery, setCountryQuery] = useState("");
  const [zipCode, setZipCode] = useState(data.zipCode ?? "");
  const [postalCodeError, setPostalCodeError] = useState<
    "required" | "invalid" | null
  >(null);

  const normalizedZipCode = normalizePostalCode(country.code, zipCode);

  // The IP answer lands after the first paint. It replaces the device's
  // guess only while the step is untouched; once she has picked a country or
  // typed a postal code, the default stays out of her way.
  useEffect(() => {
    if (
      detected.source !== "ip" ||
      hasCountryInteraction.current ||
      hasPostalInteraction.current
    ) {
      return;
    }
    const nextCountry = findCountry(detected.countryCode);
    if (nextCountry) setCountry(nextCountry);
  }, [detected.countryCode, detected.source]);

  useEffect(() => {
    if (!showCountryPicker) {
      setCountryQuery("");
      return;
    }

    countrySearchInputRef.current?.focus();
  }, [showCountryPicker]);

  const filteredCountries = useMemo(() => {
    const query = countryQuery.trim().toLocaleLowerCase();
    if (!query) return COUNTRIES;

    return COUNTRIES.filter((candidate) => {
      const translatedName = getCountryName(
        candidate,
        locale,
      ).toLocaleLowerCase();
      return (
        translatedName.includes(query) ||
        candidate.code.toLocaleLowerCase().includes(query)
      );
    });
  }, [countryQuery, locale]);

  const handleContinue = () => {
    if (country.postalRequired && !normalizedZipCode) {
      setPostalCodeError("required");
      return;
    }
    if (
      normalizedZipCode &&
      !isPostalCodeValid(country.code, normalizedZipCode)
    ) {
      setPostalCodeError("invalid");
      return;
    }

    setPostalCodeError(null);
    updateField("country", country.code);
    updateField("zipCode", normalizedZipCode || null);
    navigation.navigate("CreateAccount");
  };

  return (
    <OnboardingLayout>
      <ScreenTitle>{t("onboarding.location.title")}</ScreenTitle>
      <ScreenSubtitle>
        {country.postalRequired
          ? t("onboarding.location.subtitle", {
              params: { label: country.postalLabel.toLowerCase() },
            })
          : t("onboarding.location.postal_code_optional_subtitle")}
      </ScreenSubtitle>

      <View
        style={[
          GLASS_CONTROL_SHELL_STYLE,
          postalCodeError && {
            borderColor: "rgba(248, 113, 113, 0.72)",
            borderTopColor: "rgba(248, 113, 113, 0.86)",
          },
        ]}
      >
        <Pressable
          testID="onboarding-location-country-picker"
          onPress={() => {
            // The control shows only a flag and a chevron, so its testID is
            // the most human-readable stable identity available.
            capturePress("onboarding-location-country-picker");
            setShowCountryPicker((v) => !v);
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 12,
            paddingVertical: 14,
            gap: 4,
            borderRightWidth: 1,
            borderRightColor: semanticColors.onDark.fill.strong,
          }}
        >
          <Text style={{ fontSize: 20 }}>{country.flag}</Text>
          <ChevronDown size={14} color={semanticColors.onDark.text.tertiary} />
        </Pressable>
        <TextInput
          testID="onboarding-location-postal-code"
          value={zipCode}
          onChangeText={(value) => {
            hasPostalInteraction.current = true;
            setZipCode(value);
            if (postalCodeError) setPostalCodeError(null);
          }}
          placeholder={
            country.postalRequired
              ? country.postalPlaceholder
              : t("onboarding.location.postal_code_optional_placeholder")
          }
          placeholderTextColor={semanticColors.onDark.text.tertiary}
          selectionColor="rgba(255, 218, 189, 0.82)"
          keyboardType={country.postalKeyboard}
          maxLength={country.postalMaxLength}
          autoFocus
          autoComplete="postal-code"
          textContentType="postalCode"
          style={[GLASS_CONTROL_INPUT_STYLE, { flex: 1 }]}
        />
      </View>

      {postalCodeError && (
        <FieldMessage
          testID="onboarding-location-postal-error"
          onDark
          centered
          style={{ marginTop: 8 }}
        >
          {t(
            postalCodeError === "required"
              ? "onboarding.location.postal_code_required"
              : "onboarding.location.postal_code_invalid",
          )}
        </FieldMessage>
      )}

      {showCountryPicker && (
        <View
          style={{
            marginTop: 8,
            maxHeight: 280,
            backgroundColor: "rgba(15, 11, 9, 0.18)",
            borderRadius: 18,
            borderWidth: 1,
            borderColor: "rgba(255, 239, 225, 0.15)",
            borderTopColor: "rgba(255, 244, 236, 0.28)",
            borderBottomColor: "rgba(255, 225, 207, 0.10)",
            overflow: "hidden",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              paddingHorizontal: 14,
              paddingVertical: 10,
              borderBottomWidth: 1,
              borderBottomColor: semanticColors.onDark.fill.subtle,
            }}
          >
            <Search
              size={15}
              color={semanticColors.onDark.text.tertiary}
              strokeWidth={1.7}
            />
            <TextInput
              ref={countrySearchInputRef}
              testID="onboarding-location-country-search"
              value={countryQuery}
              onChangeText={setCountryQuery}
              placeholder={t("onboarding.location.country_search_placeholder")}
              placeholderTextColor={semanticColors.onDark.text.tertiary}
              autoCorrect={false}
              spellCheck={false}
              autoFocus
              style={{
                flex: 1,
                fontSize: 14,
                color: semanticColors.onDark.text.primary,
                fontFamily: "DMSans_400Regular",
                ...(Platform.OS === "web" ? { outlineStyle: "none" } : null),
              }}
            />
          </View>
          <ScrollView
            showsVerticalScrollIndicator
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
          >
            {filteredCountries.map((c, i) => (
              <Pressable
                key={c.code}
                testID={`onboarding-location-country-${c.code}`}
                onPress={() => {
                  // The country name is localised, so the funnel keys off the
                  // stable control identifier rather than the visible copy.
                  capturePress(
                    pressLabel(getCountryName(c, locale)),
                    "onboarding_location_country_option",
                  );
                  hasCountryInteraction.current = true;
                  setCountry(c);
                  setZipCode("");
                  setPostalCodeError(null);
                  setShowCountryPicker(false);
                }}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  borderTopWidth: i > 0 ? 1 : 0,
                  borderTopColor: semanticColors.onDark.fill.subtle,
                  backgroundColor:
                    c.code === country.code
                      ? semanticColors.onDark.fill.subtle
                      : "transparent",
                }}
              >
                <Text style={{ fontSize: 18 }}>{c.flag}</Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    flex: 1,
                    fontSize: 15,
                    color: semanticColors.onDark.text.primary,
                    fontWeight: c.code === country.code ? "600" : "400",
                  }}
                >
                  {getCountryName(c, locale)}
                </Text>
              </Pressable>
            ))}
            {filteredCountries.length === 0 && (
              <View testID="onboarding-location-country-no-matches">
                <SupportingSubtitleText
                  style={{
                    marginTop: 0,
                    paddingHorizontal: 16,
                    paddingVertical: 14,
                    color: semanticColors.onDark.text.secondary,
                    textAlign: "center",
                  }}
                >
                  {t("onboarding.location.country_search_no_matches", {
                    params: { query: countryQuery },
                  })}
                </SupportingSubtitleText>
              </View>
            )}
          </ScrollView>
        </View>
      )}

      <ContinueButton onPress={handleContinue} />
    </OnboardingLayout>
  );
}
